import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ClipboardCopy, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

import type { ActionCard } from "@/types/actions";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Textarea } from "@/components/ui/textarea";
import { useSendMessage } from "@/components/draft/use-send-message";
import { orpc } from "@/client";
import { cn } from "@/lib/utils";

function ActionSend({ action, text }: { action: ActionCard; text: string }) {
	const queryClient = useQueryClient();
	const operation = useRef(crypto.randomUUID());
	const { mutate, isPending, data, error } = useSendMessage({
		onSuccess: (result) => {
			if (result.outcome === "blocked") {
				operation.current = crypto.randomUUID();
			}
			queryClient.invalidateQueries({ queryKey: orpc.today.key() });
			queryClient.invalidateQueries({
				queryKey: orpc.cockpit.personActions.key({ input: { person_id: action.person_id } }),
			});
			queryClient.invalidateQueries({
				queryKey: orpc.timeline.forPerson.key({ input: { person_id: action.person_id } }),
			});
		},
	});
	const errorText =
		error !== null &&
		"code" in error &&
		(error.code === "ACTION_CHANGED" || error.code === "ACTION_NOT_AVAILABLE")
			? "a pendência mudou. atualize a fila antes de enviar."
			: "não foi possível enviar a mensagem";

	return (
		<div className="flex flex-col items-end gap-2">
			<ConfirmationDialog
				slot="action-send"
				tone="neutral"
				title="Enviar mensagem?"
				description={`Vai para o WhatsApp de ${action.name}.`}
				confirmLabel="Enviar"
				pendingLabel="Enviando..."
				isPending={isPending}
				onConfirm={() =>
					mutate({
						operation_id: operation.current,
						person_id: action.person_id,
						text,
						draft_text: action.draft,
						channel: "free",
						action_id: action.id,
						action_version: action.version,
					})
				}
				disabled={!text.trim() || isPending || (data !== undefined && data.outcome !== "blocked")}
				className={cn(
					"shrink-0 gap-2 rounded-md bg-whatsapp px-3 text-sm font-medium text-whatsapp-foreground transition-colors",
					"hover:bg-whatsapp/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
					"disabled:cursor-not-allowed disabled:opacity-50",
				)}
				ariaLabel="Enviar mensagem"
			>
				{isPending && <Loader2 className="size-4 animate-spin" />}
				{!isPending && data?.outcome === "sent" && <Check className="size-4" />}
				{!isPending && data?.outcome !== "sent" && <Send className="size-4" />}
				{isPending && "Enviando..."}
				{!isPending && "Enviar"}
			</ConfirmationDialog>
			{error !== null && (
				<p role="alert" className="text-xs text-destructive">
					{errorText}
				</p>
			)}
			{data?.outcome === "pending" && (
				<p className="text-xs text-muted-foreground">
					Envio registrado. A confirmação aparece na conversa.
				</p>
			)}
			{data?.outcome === "blocked" && (
				<p role="alert" className="text-xs text-destructive">
					a mensagem não foi enviada
				</p>
			)}
			{data?.outcome === "simulated" && (
				<p className="text-xs text-muted-foreground">
					simulação concluída. a mensagem não foi enviada.
				</p>
			)}
		</div>
	);
}

export function ActionChatBar({ action }: { action: ActionCard }) {
	const [text, setText] = useState(action.draft ?? "");
	const [copied, setCopied] = useState(false);

	const handleCopy = async () => {
		await navigator.clipboard.writeText(text);
		setCopied(true);
	};

	return (
		<div
			data-component="action-chat-bar"
			className="rounded-lg border border-border bg-background/60 p-3"
		>
			<label
				htmlFor={`action-draft-${action.id}`}
				className="text-xs font-medium text-muted-foreground"
			>
				Rascunho
			</label>
			<Textarea
				id={`action-draft-${action.id}`}
				value={text}
				onChange={(event) => {
					setText(event.target.value);
					setCopied(false);
				}}
				rows={1}
				className="mt-2 max-h-48 min-h-16 border-transparent p-1 leading-relaxed shadow-none [field-sizing:content]"
			/>
			<div className="mt-2 flex flex-wrap items-center justify-end gap-2">
				<button
					type="button"
					data-slot="action-copy"
					onClick={() =>
						handleCopy().catch(() => toast.error("Não foi possível copiar o rascunho"))
					}
					disabled={!text}
					aria-label="Copiar rascunho"
					className="inline-flex min-h-11 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:min-h-8"
				>
					{copied && <Check className="size-3.5 text-info" />}
					{!copied && <ClipboardCopy className="size-3.5" />}
					{copied && "Copiado"}
					{!copied && "Copiar"}
				</button>

				{action.window_open && <ActionSend action={action} text={text} />}
			</div>
		</div>
	);
}
